import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useAtomValue } from "jotai";
import { localeAtom } from "../../stores/locale";
import { authAtom } from "../../stores/auth";
import { onboardingApi, orgApi } from "../../api/client";
import CosmicButton from "../../components/CosmicButton";
import { track } from "../../lib/posthog";
import { t } from "../../lib/i18n";
import EisYamlAiGuide from "../settings/EisYamlAiGuide";

// First-run flow. The galaxy has NO observation yet (analysis is async), so the
// steps don't show the viewer's own gravity — instead they do the reframing the
// product lives or dies on: ace is a telescope (observation), not a scoreboard
// (evaluation). The org flow is welcome → boundary (disclosure) → darkmatter →
// signaling → complete; a personal galaxy drops boundary (no team to disclose).
// The concrete ordered list is built per galaxy type as `steps` below. (Setting
// up the first scan — CLI token / App install — lives in Settings / Repos, not
// in the first-run flow.)
type Step = "welcome" | "boundary" | "darkmatter" | "signaling" | "complete";

export default function Onboarding({ onComplete }: { onComplete?: () => void } = {}) {
  const { account = "" } = useParams<{ account: string }>();
  const locale = useAtomValue(localeAtom);
  const auth = useAtomValue(authAtom);
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("welcome");
  const [visibilityMode, setVisibilityMode] = useState("observation");
  const [completing, setCompleting] = useState(false);

  // Whether THIS galaxy is a personal galaxy — the only thing the first-run flow
  // still keys off (the personal flow drops the team disclosure boundary). null =
  // not loaded yet (no flicker before we know).
  const [connectInfo, setConnectInfo] = useState<{ type: string; connected: boolean } | null>(null);
  useEffect(() => {
    if (!account) return;
    setConnectInfo(null);
    orgApi
      .memberships()
      .then((ms) => {
        const m = ms.find((x) => x.login === account);
        setConnectInfo(m ? { type: m.type, connected: m.connected } : null);
      })
      .catch(() => {});
  }, [account]);

  const isPersonal = connectInfo?.type === "personal";

  // The boundary step sets the galaxy-WIDE disclosure default — a TEAM concept.
  // A personal galaxy has one subject (the owner, who always sees their own data
  // in full), so there is nobody to disclose-or-anonymise: skip boundary and lead
  // with "observe your own gravity". An org leads WITH the boundary so the
  // anti-evaluation tone is set before any team data lands.
  const steps: Step[] = isPersonal
    ? ["welcome", "darkmatter", "signaling", "complete"]
    : ["welcome", "boundary", "darkmatter", "signaling", "complete"];
  const currentIndex = steps.indexOf(step);

  // Persist, then show the reframed completion screen (not straight to the app)
  // so the closing "observation, not verdict" message lands.
  const handleComplete = async () => {
    if (!account) return;
    setCompleting(true);
    try {
      await onboardingApi.complete(account, visibilityMode);
      track.onboardingCompleted();
      setStep("complete");
    } catch {
      /* ignore */
    }
    setCompleting(false);
  };

  const finish = () => {
    if (onComplete) onComplete();
    else navigate(`/${auth.user?.login || ""}`);
  };

  return (
    <div className="mx-auto flex max-h-[calc(100vh-8rem)] items-center justify-center p-lg">
      <div className="w-full max-w-[32rem]">
        {/* Progress */}
        <div className="mb-3xl flex items-center justify-center gap-md">
          {steps.map((s, i) => (
            <div
              key={s}
              className={`h-2 w-10 rounded-full transition-colors ${
                i <= currentIndex ? "bg-gruvbox-yellow" : "bg-gruvbox-bg3"
              }`}
            />
          ))}
        </div>

        <div className="rounded-xl border border-gruvbox-bg2 bg-gruvbox-bg1 p-2xl">
          {/* Step 1: What this is — telescope, not scoreboard */}
          {step === "welcome" && (
            <>
              <div className="text-3xl">🔭</div>
              <h2 className="mt-md text-xl font-bold text-gruvbox-fg">
                {t("onboarding.welcomeTitle", locale)}
              </h2>
              <p className="mt-md text-sm text-gruvbox-fg1">
                {t("onboarding.welcomeBody", locale)}
              </p>
              <div className="mt-xl space-y-md">
                {[
                  t("onboarding.welcomePoint1", locale),
                  t("onboarding.welcomePoint2", locale),
                  t("onboarding.welcomePoint3", locale),
                ].map((line) => (
                  <div key={line} className="flex gap-md text-sm text-gruvbox-fg1">
                    <span className="text-gruvbox-yellow">·</span>
                    <span>{line}</span>
                  </div>
                ))}
              </div>
              <div className="mt-xl rounded-md bg-gruvbox-bg p-lg">
                <p className="text-sm text-gruvbox-fg1">
                  {isPersonal ? (
                    t("onboarding.welcomePersonal", locale)
                  ) : (
                    <>
                      <span className="text-gruvbox-yellow">Admin</span>
                      {t("onboarding.welcomeAdmin", locale)}
                    </>
                  )}
                </p>
              </div>
              <CosmicButton
                onClick={() => setStep(isPersonal ? "darkmatter" : "boundary")}
                variant="primary"
                size="md"
                className="mt-2xl w-full"
              >
                {t("onboarding.continue", locale)}
              </CosmicButton>
            </>
          )}

          {/* Step 2: The disclosure boundary — you decide, default anonymous */}
          {step === "boundary" && (
            <>
              <h2 className="text-xl font-bold text-gruvbox-fg">
                {t("onboarding.boundaryTitle", locale)}
              </h2>
              <p className="mt-md text-sm text-gruvbox-fg1">
                {t("onboarding.boundaryBody", locale)}
              </p>
              <div className="mt-lg space-y-md">
                {[
                  {
                    id: "observation",
                    title: "Observation",
                    rec: true,
                    desc: t("onboarding.modeObservationDesc", locale),
                  },
                  {
                    id: "context",
                    title: "Context",
                    desc: t("onboarding.modeContextDesc", locale),
                  },
                  {
                    id: "full_insight",
                    title: "Full Insight",
                    desc: t("onboarding.modeFullInsightDesc", locale),
                  },
                ].map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => setVisibilityMode(mode.id)}
                    className={`w-full rounded-lg border p-lg text-left transition-colors ${
                      visibilityMode === mode.id
                        ? "border-gruvbox-yellow bg-gruvbox-bg2"
                        : "border-gruvbox-bg3 bg-gruvbox-bg hover:border-gruvbox-bg2"
                    }`}
                  >
                    <p className="flex items-center gap-md text-sm font-medium text-gruvbox-fg">
                      {mode.title}
                      {mode.rec && (
                        <span className="rounded bg-gruvbox-bg3 px-sm py-xs text-[10px] text-gruvbox-yellow">
                          {t("onboarding.recommended", locale)}
                        </span>
                      )}
                    </p>
                    <p className="mt-sm text-xs text-gruvbox-gray">{mode.desc}</p>
                  </button>
                ))}
              </div>
              <p className="mt-md text-xs text-gruvbox-gray">
                {t("onboarding.boundarySettingsNote", locale)}
              </p>
              <div className="mt-2xl space-y-md">
                <CosmicButton
                  onClick={() => setStep("darkmatter")}
                  variant="primary"
                  size="md"
                  className="w-full"
                >
                  {t("onboarding.continue", locale)}
                </CosmicButton>
                <button
                  onClick={() => setStep("welcome")}
                  className="block w-full text-center text-sm text-gruvbox-gray hover:text-gruvbox-fg1"
                >
                  {t("onboarding.back", locale)}
                </button>
              </div>
            </>
          )}

          {/* Step 3: What ace can't see — the anti-evaluation contract */}
          {step === "darkmatter" && (
            <>
              <h2 className="text-xl font-bold text-gruvbox-fg">
                {t("onboarding.darkmatterTitle", locale)}
              </h2>
              <p className="mt-md text-sm text-gruvbox-fg1">
                {t("onboarding.darkmatterBody", locale)}
              </p>
              <div className="mt-lg space-y-md">
                {[
                  t("onboarding.darkmatterItem1", locale),
                  t("onboarding.darkmatterItem2", locale),
                  t("onboarding.darkmatterItem3", locale),
                  t("onboarding.darkmatterItem4", locale),
                ].map((line) => (
                  <div key={line} className="flex gap-md text-sm text-gruvbox-fg1">
                    <span className="text-gruvbox-gray">·</span>
                    <span>{line}</span>
                  </div>
                ))}
              </div>
              <div className="mt-xl rounded-md border border-gruvbox-yellow/40 bg-gruvbox-bg p-lg">
                <p className="text-sm text-gruvbox-fg">
                  {t("onboarding.darkmatterPartialView", locale)}
                </p>
              </div>
              <div className="mt-2xl space-y-md">
                <CosmicButton
                  onClick={() => setStep("signaling")}
                  variant="primary"
                  size="md"
                  className="w-full"
                >
                  {t("onboarding.darkmatterContinue", locale)}
                </CosmicButton>
                <button
                  onClick={() => setStep(isPersonal ? "welcome" : "boundary")}
                  className="block w-full text-center text-sm text-gruvbox-gray hover:text-gruvbox-fg1"
                >
                  {t("onboarding.back", locale)}
                </button>
              </div>
            </>
          )}

          {/* Step 4: Tune signaling (eis.yaml) — optional but recommended.
              Completion (handleComplete) is fired exactly once from this step's
              Next/Skip, so the API call and setStep("complete") never double-fire. */}
          {step === "signaling" && (
            <>
              <h2 className="text-xl font-bold text-gruvbox-fg">
                {t("onboarding.signaling.title", locale)}
              </h2>
              <p className="mt-md text-sm text-gruvbox-fg1">
                {t("onboarding.signaling.body", locale)}
              </p>

              <div className="mt-lg">
                <EisYamlAiGuide uploadHint={t("settings.eisAgent.step4Onboarding", locale)} />
              </div>

              <div className="mt-2xl space-y-md">
                <CosmicButton
                  onClick={handleComplete}
                  disabled={completing}
                  variant="primary"
                  size="md"
                  className="w-full"
                >
                  {completing
                    ? t("onboarding.beginning", locale)
                    : t("onboarding.signaling.next", locale)}
                </CosmicButton>
                <button
                  onClick={handleComplete}
                  disabled={completing}
                  className="block w-full text-center text-sm text-gruvbox-gray hover:text-gruvbox-fg1 disabled:opacity-50"
                >
                  {t("onboarding.signaling.skip", locale)}
                </button>
                <button
                  onClick={() => setStep("darkmatter")}
                  className="block w-full text-center text-sm text-gruvbox-gray hover:text-gruvbox-fg1"
                >
                  {t("onboarding.back", locale)}
                </button>
              </div>
            </>
          )}

          {/* Step 5: First observation forming — a question, not a verdict */}
          {step === "complete" && (
            <>
              <div className="text-center">
                <div className="text-4xl">🔭</div>
                <h2 className="mt-lg text-xl font-bold text-gruvbox-fg">
                  {t("onboarding.completeTitle", locale)}
                </h2>
                <p className="mt-md text-sm text-gruvbox-fg1">
                  {t("onboarding.completeBody", locale)}
                </p>
              </div>
              <CosmicButton
                onClick={finish}
                variant="primary"
                size="md"
                className="mt-2xl w-full"
              >
                {t("onboarding.openObservatory", locale)}
              </CosmicButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
